import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Card, 
  Grid, 
  Box, 
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  Pagination
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { FAST_API_BASE_URL } from "constants";
import axios from "axios";
import CustomdateD from "../Dashboard/CustomdateD";
import { useQueryParams } from "context/QueryParamsContext";
import { useLocation, useNavigate } from "react-router-dom";
import { enUS } from "date-fns/locale";
import dayjs from "dayjs";
import wordpress_logo from "../../../assets/images/wordpress_logo.png";
import webflow_logo from "../../../assets/images/webflow-logo.png";
import square_space_logo from "../../../assets/images/squarespace-logo.png";
import wix_logo from "../../../assets/images/wix-logo.png";

const sourceIcons = {
  wordpress: wordpress_logo,
  webflow: webflow_logo,
  wix: wix_logo,
  squarespace: square_space_logo,
  others: "https://www.bing.com/th/id/OIP.MjxSzLp-sdoAzGREfXBUAAHaHa?w=200&h=211&c=8&rs=1&qlt=90&r=0&o=6&cb=12&pid=3.1&rm=2",
  uncategorized: "https://cdn-icons-png.flaticon.com/512/3524/3524335.png",
};

const sourceDisplayNames = {
  wordpress: "WordPress",
  webflow: "Webflow",
  wix: "Wix",
  squarespace: "Squarespace",
  custom: "Others",
  uncategorized: "Uncategorized"
};

const SOURCE_ORDER = ['wordpress', 'webflow', 'wix', 'squarespace', 'custom'];

const DomainStatCard = ({ title, count, icon, onClick, isSelected }) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: "pointer",
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        border: isSelected ? "2px solid #219197" : "2px solid transparent",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 20px 27px 0 rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <SoftBox paddingY={1} paddingX={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <SoftBox>
              <SoftTypography
                variant="caption"
                color="text"
                fontWeight="bold"
                textTransform="uppercase"
                sx={{ fontSize: "0.75rem" }}
              >
                {title}
              </SoftTypography>
              <SoftTypography
                variant="h4"
                fontWeight="bold"
                sx={{
                  mt: 0.1,
                  background: "linear-gradient(195deg, #219297, #219197)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {count.toLocaleString()}
              </SoftTypography>
            </SoftBox>
          </Grid>
          <Grid item>
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3.5rem"
              height="3.5rem"
              borderRadius="lg"
              shadow="md"
              sx={{
                background: isSelected 
                  ? "linear-gradient(195deg, #cfdde0ff, #cbd8dbff)"
                  : "linear-gradient(195deg, #ffffff, #f8f9fa)",
                border: "1px solid #e9ecef",
              }}
            >
              <img
                src={icon}
                alt={title}
                style={{
                  width: "48px",
                  height: "48px",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.src = sourceIcons.others;
                }}
              />
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
};

const DomainsBySource = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  
  const navigate = useNavigate();
  const { dateRange, setDateRange } = useQueryParams();
  const location = useLocation();

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    localStorage.setItem("saved_start_date", start);
    localStorage.setItem("saved_end_date", end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("start_date", start);
    queryParams.set("end_date", end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  const { start_date, end_date } = dateRange;

  const sortDataByPredefinedOrder = (apiData) => {
    return SOURCE_ORDER.map(source => {
      const foundItem = apiData.find(item => 
        item.source?.toLowerCase() === source
      );
      
      return foundItem || { 
        source: source, 
        count: 0 
      };
    });
  };

  const fetchDomainsBySource = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${FAST_API_BASE_URL}/api/domains-count-by-source`;
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": process.env.REACT_APP_API_KEY,
        },
        params: {
          start_date,
          end_date
        }
      });

      const result = response.data;
      if (result.resultType === "Success") {
        const sortedData = sortDataByPredefinedOrder(result.result);
        setData(sortedData);
      } else {
        throw new Error(result.messages?.[0] || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching domains by source:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainDetails = useCallback(async (source, pageNum = 1, limit = 10, sortKey = "created_at", sortDirection = "desc") => {
    try {
      setLoadingDomains(true);
      const response = await axios.get(`${FAST_API_BASE_URL}/api/domains-by-source`, {
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": process.env.REACT_APP_API_KEY,
        },
        params: {
          source,
          start_date,
          end_date,
          limit,
          page: pageNum, // Add page parameter
          sort_key: sortKey,
          sort_direction: sortDirection
        }
      });

      if (response.data.resultType === "Success") {
        const apiData = response.data.result;
        setDomains(apiData.data || []);
        setTotalEntries(apiData.total || 0);
      }
    } catch (err) {
      console.error("Error fetching domain details:", err);
      setDomains([]);
      setTotalEntries(0);
    } finally {
      setLoadingDomains(false);
    }
  }, [start_date, end_date]);

  const handleCardClick = (source) => {
    if (selectedSource === source) {
      setSelectedSource(null);
      setDomains([]);
      setTotalEntries(0);
      setSearch("");
      setPage(1);
    } else {
      setSelectedSource(source);
      setSearch("");
      setPage(1);
      fetchDomainDetails(source, 1, entriesPerPage, sortConfig.key, sortConfig.direction);
    }
  };

  useEffect(() => {
    fetchDomainsBySource();
    setSelectedSource(null);
    setDomains([]);
    setTotalEntries(0);
    setSearch("");
    setPage(1);
  }, [start_date, end_date]);

  // Fetch domains when pagination, sorting, or entries per page changes
  useEffect(() => {
    if (selectedSource) {
      fetchDomainDetails(selectedSource, page, entriesPerPage, sortConfig.key, sortConfig.direction);
    }
  }, [page, entriesPerPage, sortConfig, selectedSource, fetchDomainDetails]);

  const filteredDomains = useMemo(() => {
    if (!search) return domains;
    return domains.filter(domain => 
      domain.domain_name?.toLowerCase().includes(search.toLowerCase()) ||
      domain.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      domain.time_zone?.toLowerCase().includes(search.toLowerCase())
    );
  }, [domains, search]);

  const displayDomains = useMemo(() => {
    return search ? filteredDomains : domains;
  }, [domains, filteredDomains, search]);

  // Calculate pagination for client-side search
  const totalPagesForSearch = Math.ceil(displayDomains.length / entriesPerPage);
  const startForSearch = displayDomains.length === 0 ? 0 : (page - 1) * entriesPerPage + 1;
  const endForSearch = Math.min(page * entriesPerPage, displayDomains.length);

  // Paginate search results client-side
  const paginatedSearchDomains = useMemo(() => {
    if (!search) return displayDomains;
    const startIndex = (page - 1) * entriesPerPage;
    return displayDomains.slice(startIndex, startIndex + entriesPerPage);
  }, [displayDomains, page, entriesPerPage, search]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSort = (columnKey) => {
    const newDirection = sortConfig.key === columnKey 
      ? sortConfig.direction === "desc" ? "asc" : "desc"
      : "desc";
    
    setSortConfig({
      key: columnKey,
      direction: newDirection,
    });
    setPage(1);
  };

  const formatDate = (dateString) => {
    return dayjs(dateString).format("MMM D, YYYY hh:mm A");
  };

  const domainsToDisplay = search ? paginatedSearchDomains : displayDomains;
  const totalPages = search ? totalPagesForSearch : Math.ceil(totalEntries / entriesPerPage);
  const start = search ? startForSearch : (totalEntries === 0 ? 0 : (page - 1) * entriesPerPage + 1);
  const end = search ? endForSearch : Math.min(page * entriesPerPage, totalEntries);
  const totalCount = search ? displayDomains.length : totalEntries;

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
          <CircularProgress sx={{ color: "#17c1e8" }} />
        </Box>
      </DashboardLayout>
    );
  }

  const selectedSourceData = data.find(item => item.source === selectedSource);
  const selectedDisplayName = selectedSourceData 
    ? sourceDisplayNames[selectedSourceData.source?.toLowerCase()] || selectedSourceData.source 
    : "";

  const columns = [
    { Header: "Domain Name", accessor: "domain_name", align: "left" },
    { Header: "Company Name", accessor: "company_name", align: "left" },
    { Header: "Time Zone", accessor: "time_zone", align: "left" },
    { Header: "Created At", accessor: "created_at", align: "left" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <CustomdateD onRangeSelect={handleDateRangeSelect} />
        </SoftBox>

        {error && (
          <SoftBox mb={3}>
            <Card sx={{ backgroundColor: "#f44336" }}>
              <SoftBox p={3}>
                <SoftTypography color="white" variant="h6" fontWeight="medium">
                  Error: {error}
                </SoftTypography>
                <button
                  onClick={() => fetchDomainsBySource()}
                  style={{
                    marginTop: "12px",
                    padding: "8px 16px",
                    backgroundColor: "#ffffff",
                    color: "#f44336",
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  Retry
                </button>
              </SoftBox>
            </Card>
          </SoftBox>
        )}

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {data.map((item, index) => {
              const sourceKey = item.source?.toLowerCase() || "uncategorized";
              const displayName = sourceDisplayNames[sourceKey] || item.source || "Uncategorized";
              const icon = sourceIcons[sourceKey] || sourceIcons.others;

              return (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <DomainStatCard 
                    title={displayName} 
                    count={item.count || 0} 
                    icon={icon}
                    onClick={() => handleCardClick(item.source)}
                    isSelected={selectedSource === item.source}
                  />
                </Grid>
              );
            })}

            {data.length === 0 && !loading && (
              <Grid item xs={12}>
                <Card>
                  <SoftBox p={5} textAlign="center">
                    <SoftTypography variant="h6" color="text" fontWeight="regular">
                      No domain data available for the selected period.
                    </SoftTypography>
                  </SoftBox>
                </Card>
              </Grid>
            )}
          </Grid>
        </SoftBox>

        {selectedSource && (
          <Card sx={{ boxShadow: "none", minHeight: "450px" }}>
            <SoftBox p={3} lineHeight={1.2}>
              {loadingDomains ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <CircularProgress sx={{ color: "#17c1e8" }} />
                </Box>
              ) : (
                <>
                  <h4 style={{ textAlign: "left", padding: "10px 0" }}>
                    {selectedDisplayName} Domains ({totalCount.toLocaleString()})
                  </h4>

                  <SoftBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    flexWrap="wrap"
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                    gap={2}
                    pb={2}
                  >
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      <SoftSelect
                        defaultValue={{
                          value: entriesPerPage,
                          label: entriesPerPage,
                        }}
                        options={[5, 10, 15, 20, 25].map((entry) => ({
                          value: entry,
                          label: entry,
                        }))}
                        onChange={handleEntriesPerPageChange}
                        size="small"
                      />
                      <SoftTypography variant="caption" color="secondary" ml={1}>
                        entries per page
                      </SoftTypography>
                    </SoftBox>

                    <SoftBox sx={{ width: { xs: "100%", md: "12rem" } }}>
                      <SoftInput
                        placeholder="Search..."
                        value={search}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    </SoftBox>
                  </SoftBox>

                  {domainsToDisplay.length > 0 ? (
                    <>
                      <Table>
                        <SoftBox component="thead">
                          <TableRow>
                            {columns.map((column, key) => (
                              <DataTableHeadCell
                                key={key}
                                width={column.width || "auto"}
                                align={column.align || "left"}
                                onClick={() => handleSort(column.accessor)}
                                sorted={
                                  sortConfig.key === column.accessor
                                    ? sortConfig.direction
                                    : "none"
                                }
                                style={{
                                  cursor: "pointer",
                                  userSelect: "none",
                                }}
                              >
                                {column.Header}
                              </DataTableHeadCell>
                            ))}
                          </TableRow>
                        </SoftBox>

                        <TableBody>
                          {domainsToDisplay.map((domain, key) => (
                            <TableRow key={key} sx={{ padding: 4 }}>
                              {columns.map((col, index) => (
                                <DataTableBodyCell
                                  key={index}
                                  align={col.align || "left"}
                                >
                                  {col.accessor === "created_at"
                                    ? formatDate(domain[col.accessor])
                                    : domain[col.accessor]}
                                </DataTableBodyCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <SoftBox
                        pb={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        paddingTop="10px"
                        paddingLeft="40px"
                      >
                        <SoftTypography variant="button" color="secondary">
                          Showing {new Intl.NumberFormat(enUS).format(start)} to{" "}
                          {new Intl.NumberFormat(enUS).format(end)} of{" "}
                          {new Intl.NumberFormat(enUS).format(totalCount)} entries
                        </SoftTypography>
                      </SoftBox>

                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          shape="circular"
                          color="info"
                          sx={{
                            "& .MuiPaginationItem-root": {
                              "&.Mui-selected": {
                                background: "#219297",
                                color: "white !important",
                                "&:hover": {
                                  background: "teal",
                                },
                              },
                              "&:not(.Mui-selected)": {
                                color: "black",
                              },
                            },
                          }}
                        />
                        <SoftTypography variant="button">
                          Page {new Intl.NumberFormat(enUS).format(page)} of{" "}
                          {new Intl.NumberFormat(enUS).format(totalPages)}
                        </SoftTypography>
                      </SoftBox>
                    </>
                  ) : (
                    <Box py={5} textAlign="center">
                      <SoftTypography variant="button" color="text">
                        {search ? "No matching domains found" : "No domains found for this source"}
                      </SoftTypography>
                    </Box>
                  )}
                </>
              )}
            </SoftBox>
          </Card>
        )}
      </SoftBox>
    </DashboardLayout>
  );
};

export default DomainsBySource;